import React, { useMemo, useState } from 'react';
import Select from 'react-select';
import { UnitTypeUiModel } from '../../../../../shared/ui-models/unit-type.ui-model';
import { ReservationMode } from '../../../../../shared/enums/reservation-mode.enum';
import { getAllUnitTypes } from '../../../../../shared/services/data/unit-type.data';
import { isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { getModuleName } from '../../../../shared/services/reservation.service';
import {
  CHIP,
  EMPTY_STATE,
  FIELD,
  LABEL,
  SECTION_TITLE,
  TD,
  TH,
  formatMoney,
  selectProps
} from '../../reservation-create.ui';

export interface LookupOption {
  value: string;
  label: string;
  areaId: string;
}

export interface UnitSectionProps {
  data: {
    areas: LookupOption[];
    projects: LookupOption[];
    selectedUnitType?: UnitTypeUiModel;
  };
  eventHandlers: {
    onSelectUnitType: (unitType: UnitTypeUiModel) => void;
  };
}

const matchesSearch = (unitType: UnitTypeUiModel, search: string) => {
  if (!search) return true;
  const value = search.toLowerCase();
  return (
    unitType.name?.toLowerCase().includes(value) ||
    unitType.propertyName?.toLowerCase().includes(value) ||
    unitType.area?.toLowerCase().includes(value) ||
    unitType.units?.some(
      (room) =>
        room.shortName?.toLowerCase().includes(value) ||
        room.roomNumber?.toLowerCase().includes(value) ||
        room.unitId?.toLowerCase().includes(value)
    )
  );
};

export const UnitSection: React.FC<UnitSectionProps> = ({
  data: { areas, projects, selectedUnitType },
  eventHandlers: { onSelectUnitType }
}) => {
  const [area, setArea] = useState<LookupOption | null>(null);
  const [project, setProject] = useState<LookupOption | null>(null);
  const [unitId, setUnitId] = useState('');
  const [search, setSearch] = useState('');
  const [unitTypes, setUnitTypes] = useState<UnitTypeUiModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const projectOptions = area ? projects.filter((item) => item.areaId === area.areaId) : projects;
  const visibleUnitTypes = useMemo(
    () => unitTypes.filter((unitType) => matchesSearch(unitType, search)),
    [unitTypes, search]
  );

  const loadUnitTypes = (query: { propertyId?: string; freeText?: string }) => {
    setIsLoading(true);
    setHasSearched(true);
    getAllUnitTypes(getModuleName(), 0, -1, { ...query, isManaged: true })
      .then((response) => {
        setUnitTypes(isErrorInfo(response) ? [] : response?.unitList || []);
      })
      .finally(() => setIsLoading(false));
  };

  const searchByUnitId = () => {
    const value = unitId.trim();
    if (!value) return;
    setArea(null);
    setProject(null);
    setSearch('');
    loadUnitTypes({ freeText: value });
  };

  return (
    <>
      <h2 className={SECTION_TITLE}>Unit</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-[12px] mb-[16px]">
        <label className={LABEL}>
          Area
          <Select<LookupOption, false>
            {...selectProps}
            options={areas}
            value={area}
            onChange={(value) => {
              setArea(value || null);
              setProject(null);
            }}
            placeholder="All areas"
            isClearable
            isSearchable
          />
        </label>
        <label className={LABEL}>
          Project
          <Select<LookupOption, false>
            {...selectProps}
            options={projectOptions}
            value={project}
            onChange={(value) => {
              setProject(value || null);
              setUnitId('');
              if (value) loadUnitTypes({ propertyId: value.value });
            }}
            placeholder="Select project"
            isClearable
            isSearchable
          />
        </label>
        <label className={LABEL}>
          Unit ID
          <input
            className={FIELD}
            value={unitId}
            placeholder="Press Enter to search"
            onChange={(event) => setUnitId(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && searchByUnitId()}
          />
        </label>
        <label className={LABEL}>
          Filter
          <input
            className={FIELD}
            value={search}
            placeholder="Unit type or room"
            disabled={!unitTypes.length}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
      </div>

      {isLoading ? (
        <div className={EMPTY_STATE}>Loading units…</div>
      ) : !hasSearched ? (
        <div className={EMPTY_STATE}>Select a project or search by unit ID</div>
      ) : !visibleUnitTypes.length ? (
        <div className={EMPTY_STATE}>No units found</div>
      ) : (
        <div className="overflow-x-auto rounded-[12px] border border-gray-200">
          <table className="w-full min-w-[760px] border-collapse">
            <thead className="bg-creams-100">
              <tr>
                <th className={TH}>Unit type</th>
                <th className={TH}>Location</th>
                <th className={TH}>Size</th>
                <th className={TH}>Rooms</th>
                <th className={`${TH} text-right`}>Price</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {visibleUnitTypes.map((unitType) => {
                const isSelected = selectedUnitType?.id === unitType.id;
                const isMonthly = unitType.reservationMode === ReservationMode.MONTHLY;
                return (
                  <tr
                    key={unitType.id}
                    onClick={() => onSelectUnitType(unitType)}
                    className={`cursor-pointer transition-colors ${
                      isSelected ? 'bg-primary-50' : 'bg-neutral-0 hover:bg-creams-100'
                    }`}
                  >
                    <td className={`${TD} font-gilroy-medium`}>
                      <span className="inline-flex items-center gap-[10px]">
                        <span
                          className={`size-[16px] shrink-0 rounded-full border-2 ${
                            isSelected ? 'border-primary-500 bg-primary-500' : 'border-gray-200'
                          }`}
                        />
                        <span className="max-w-[240px] truncate" title={unitType.name}>
                          {unitType.name || 'Untitled unit'}
                        </span>
                      </span>
                    </td>
                    <td className={`${TD} text-neutral-300`}>
                      <span className="max-w-[240px] truncate inline-block align-middle">
                        {[unitType.propertyName, unitType.area].filter(Boolean).join(' · ')}
                      </span>
                    </td>
                    <td className={`${TD} text-neutral-300`}>
                      {unitType.bedrooms ? `${unitType.bedrooms} BR` : 'Studio'}
                      {unitType.maxGuests ? ` · ${unitType.maxGuests} guests` : ''}
                    </td>
                    <td className={TD}>
                      <span
                        className={`${CHIP} bg-creams-100 border border-gray-200 text-neutral-500`}
                      >
                        {unitType.units?.length || 0} rooms
                      </span>
                    </td>
                    <td className={`${TD} text-right`}>
                      {unitType.unitPrice !== undefined && (
                        <>
                          <span className="font-gilroy-medium">
                            {formatMoney(unitType.unitPrice, unitType.currency)}
                          </span>
                          <span className="text-neutral-300">
                            {' '}
                            / {isMonthly ? 'month' : 'night'}
                          </span>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};
